import { useMemo } from "react";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { getNurseEffectiveStatus } from "../utils/formatters";
import { buildStaffCountByJobId } from "../utils/jobs";
import { EffectiveEmployeeStatus, type TeamNurse } from "../utils/models";
import { useDepartmentStaff } from "./useDepartmentStaff";

/**
 * Whether an employee's jobs should shape this department's popularity order.
 *
 * Membership is read from `departments` rather than inferred from the status maps, because the two
 * can legitimately disagree. The demo-department branch of `read_nurses` borrows the roster from
 * `InHouse__Hospital_Demo` and re-homes `department_ids` onto the demo department, but leaves
 * `departmentStatuses` / `departmentEmployeeStatuses` keyed by the department it borrowed from — so
 * a demo roster has membership and no status row at all. Reading that as Inactive dropped every
 * employee, which silently disabled popularity ordering on exactly the departments demos are given.
 */
function countsTowardPopularity(nurse: TeamNurse, departmentId: string): boolean {
	if (!nurse.departments?.includes(departmentId)) return false;
	const hasStatusRow = nurse.departmentEmployeeStatuses?.[departmentId] !== undefined || nurse.departmentStatuses?.[departmentId] !== undefined;
	// DEFAULT: `true` for an employee this department claims but has no status row for. Unrepresented
	// is not terminated — only an explicit Inactive status, or an explicit `active: false`, excludes
	// someone. A missing row is a data gap (or the demo re-homing above) and must not quietly shrink
	// the counts; the alternative reads absence as a termination and undercounts real staff.
	if (!hasStatusRow) return true;
	return getNurseEffectiveStatus(nurse, departmentId) !== EffectiveEmployeeStatus.Inactive;
}

/**
 * How many of a department's staff hold each job, keyed by job id — the popularity signal behind
 * `JobSelector`'s option order. Fully client-side: for the chosen department it reads the roster cache
 * entry `HospitalDataProvider` already fills, so it costs no request (see `useDepartmentStaff`).
 *
 * **Who counts.** Everyone whose effective status in the department is anything other than
 * `Inactive`. Terminated staff drop out; On Leave and Suspended staff stay, because they still hold
 * the job and will work it again.
 *
 * Status comes from `getNurseEffectiveStatus`, the app's single source of truth: it prefers the
 * backend-computed `departmentEmployeeStatuses` map and falls back to the legacy `departmentStatuses`
 * boolean only when the department has no computed entry. Calling `isNurseActiveInDepartment`
 * directly would skip the computed map altogether, so this is the better read.
 *
 * It is asked only about employees who have a status row at all, though. `getNurseEffectiveStatus`
 * resolves "absent from both maps" to Inactive, which is the right answer to *its* question and the
 * wrong one to this one — see {@link countsTowardPopularity} for why membership and status have to
 * be read separately.
 *
 * Returns `null` — never an empty map — when there is no signal at all: the roster has not loaded
 * yet (or failed to — the pages that show staff report that), or nobody in the department holds a
 * job. Callers must then decide what "no popularity" means rather than accidentally sorting by an
 * all-zero map.
 *
 * It reads the query directly rather than through `useRosterLookup`: `JobSelector` mounts it in
 * many places, and none of them needs an id map. The `useMemo` is keyed on the roster array, which
 * React Query keeps referentially stable while the data is unchanged.
 */
export function useDepartmentStaffCountByJobId(departmentId: string): ReadonlyMap<string, number> | null {
	const { staff: nurses } = useDepartmentStaff(departmentId);

	return useMemo(() => {
		if (!nurses?.length) return null;
		const employed = nurses.filter((nurse) => countsTowardPopularity(nurse, departmentId));
		const counts = buildStaffCountByJobId(employed);
		return counts.size ? counts : null;
	}, [nurses, departmentId]);
}

/** {@link useDepartmentStaffCountByJobId} for the chosen department. */
export function useStaffCountByJobId(): ReadonlyMap<string, number> | null {
	const { chosenDepartment } = useChosenDepartment();
	return useDepartmentStaffCountByJobId(chosenDepartment.department_id);
}
